import React, { useEffect, useState } from 'react';
import styles from './Styles/Photos.module.css';
import { getAllPhotos, getPhotosByUser, getPhotosByEvent, removePhoto } from '../../Services/arndAPI';
import PhotoMatchesModal from '../../Components/PhotoMatchesModal/PhotoMatchesModal';
import Loader from '../../Components/Loader/Loader';
import PageHeader from '../../Components/PageHeader/PageHeader';
import Page from '../../Components/Page/Page';

const S3_BASE = 'https://arnd-development.s3.us-west-1.amazonaws.com/';

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [filter, setFilter] = useState({ userId: '', eventId: '' });
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [modalAttendees, setModalAttendees] = useState([]);

  const fetchPhotos = async () => {
    setLoading(true);
    setStatus('');
    try {
      let data;
      if (filter.userId) {
        data = await getPhotosByUser(filter.userId);
      } else if (filter.eventId) {
        data = await getPhotosByEvent(filter.eventId);
      } else {
        data = await getAllPhotos();
      }
      setPhotos(data);
    } catch {
      setStatus('Error loading photos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if a filter is applied
    if (filter.userId || filter.eventId) {
      fetchPhotos();
    } else {
      setPhotos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleRemove = async (photoId) => {
    if (!window.confirm('Are you sure you want to remove this photo?')) return;
    setStatus('Removing photo...');
    try {
      await removePhoto(photoId);
      setStatus('Photo removed.');
      fetchPhotos();
    } catch {
      setStatus('Error removing photo.');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Page wide>
      <PageHeader title="Photos" />
      <form className={styles.filters} onSubmit={e => { e.preventDefault(); fetchPhotos(); }}>
        <input
          type="text"
          name="userId"
          placeholder="Search by Photographer ID"
          value={filter.userId}
          onChange={handleFilterChange}
        />
        <input
          type="text"
          name="eventId"
          placeholder="Search by Event ID"
          value={filter.eventId}
          onChange={handleFilterChange}
        />
        <button type="submit">Search</button>
      </form>
      {loading ? <Loader /> : (
        (filter.userId || filter.eventId) ? (
          <div className={styles.cardGrid}>
            {photos.length === 0 ? <div className={styles.emptyPhotos}><svg width="48" height="48" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ marginBottom: '1rem' }}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="2.5" /><path d="M21 15l-5-5L5 21" /></svg><div style={{ fontSize: '1.2rem', color: '#374151', fontWeight: 500 }}>No photos found</div></div> : (
              <table className={styles.photoTable}>
                <thead>
                  <tr>
                    <th>Thumbnail</th>
                    <th>Event</th>
                    <th>Created</th>
                    <th>ID</th>
                    <th>Matches</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {photos.map((photo, idx) => (
                    <tr key={photo.id + '-' + idx}>
                      <td>
                        <img
                          src={S3_BASE + (photo.thumbnailUrl || photo.mediumUrl)}
                          alt={photo.event?.name || 'Event'}
                          style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', background: '#f3f4f6', border: '1px solid #e5e7eb' }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{photo.event?.name || '-'}</div>
                        <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>{photo.event?.id || '-'}</div>
                      </td>
                      <td>{new Date(photo.createdAt).toLocaleString()}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.97rem' }}>{photo.id}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.97rem' }}>
                        {photo.hasMatch && photo.matchedAttendees && photo.matchedAttendees.length > 0 ? (
                          <span
                            style={{ color: '#0d58ecff', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                              setModalAttendees(photo.matchedAttendees);
                              setShowMatchesModal(true);
                            }}
                          >
                            Attendees
                          </span>
                        ) : (
                          'None'
                        )}
                      </td>
                      <td style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className={styles.removeBtn} onClick={() => handleRemove(photo.id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className={styles.emptyPhotos}>
            <svg width="48" height="48" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ marginBottom: '1rem' }}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="2.5" /><path d="M21 15l-5-5L5 21" /></svg>
            <div style={{ fontSize: '1.2rem', color: '#374151', fontWeight: 500 }}>Search by event or photographer to view photos</div>
            <div style={{ color: '#6b7280', fontSize: '1rem', marginTop: '0.5rem' }}>Please enter a Photographer ID or Event ID above.</div>
          </div>
        )
      )}
      {status && <div className={styles.status}>{status}</div>}
      {showMatchesModal && (
        <PhotoMatchesModal
          attendees={modalAttendees}
          onClose={() => setShowMatchesModal(false)}
        />
      )}
    </Page>
  );
}
